import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { observer, inject } from 'mobx-react';
import ButtonWithLoading from '../form-fields/ButtonWithLoading';
import CloseOneIcon from '../svg/CloseOneIcon';

const ShareCartModal = inject('wooCommerceStore', 'studioStore')(observer(({
  isOpen,
  onClose,
  wooCommerceStore,
  studioStore,
}) => {
  const t = useTranslations('Cart');
  const tg = useTranslations('General');
  const [isCreating, setIsCreating] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const container = studioStore.settings.product_popup?.container;
  const productInfoBackgroundColor = studioStore.settings.product_popup?.product_info_background_color || 'white';
  const primaryColor = studioStore.settings.colors.primary_color;

  let classes = "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md md:mx-auto max-h-[90vh] z-[60] overscroll-contain";
  const style = {
    backgroundColor: productInfoBackgroundColor,
  };

  if (container === "stamp") {
    classes = classes + " stamp-border";
    style.backgroundImage = `radial-gradient(8px, transparent 98%, ${productInfoBackgroundColor}),linear-gradient(${productInfoBackgroundColor} 0px, ${productInfoBackgroundColor} 0px)`;
    style.backgroundColor = '';
  } else {
    classes = classes + " rounded-lg shadow-lg p-4 bg-white";
  }

  const handleCreateShareLink = async () => {
    setIsCreating(true);
    setError('');
    setCopied(false);

    try {
      // Create a name for the shared cart
      const cartName = `${t('shared_cart')} - ${new Date().toLocaleDateString()}`;

      // Save the cart with share flag set to true
      const result = await wooCommerceStore.saveCart(cartName, true);

      // Generate share URL
      const frontendUrl = window.location.origin;
      const shareLink = `${frontendUrl}/shared-cart/${result.cart_id}`;

      setShareUrl(shareLink);
    } catch (err) {
      setError(err.message || t('failed_to_create_share_link'));
    } finally {
      setIsCreating(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="fixed inset-0 transition-opacity" aria-hidden="true">
        <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <div className={classes} style={style}>
        <button
          className="absolute top-4 md:-top-8 left-4 md:-left-10 rtl:right-4 rtl:md:-right-10 rtl:left-auto z-10"
          onClick={onClose}
          disabled={isCreating}
        >
          <CloseOneIcon size="28px" stroke={primaryColor} />
        </button>

        <div className="max-h-[86vh] overflow-y-auto">
          <div className="p-3 md:p-6">
            <div className="px-4 md:px-8">
              <h2 className="text-2xl font-semibold mb-6 text-center" style={{color: primaryColor}}>
                {t('share_cart')}
              </h2>

              {error && (
                <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
                  {error}
                </div>
              )}

              <p className="mb-6 text-center" style={{color: studioStore.settings.colors.text_secondary_color}}>
                {t('share_cart_description')}
              </p>

              {!shareUrl ? (
                <div className="flex justify-center mb-4">
                  <ButtonWithLoading
                    type="button"
                    onClick={handleCreateShareLink}
                    text={t('create_share_link')}
                    loading={isCreating}
                    className="px-4 py-2"
                  />
                </div>
              ) : (
                <div className="mb-6">
                  <div className="flex items-center">
                    <input
                      type="text"
                      readOnly
                      value={shareUrl}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none"
                    />
                    <ButtonWithLoading
                      type="button"
                      onClick={copyToClipboard}
                      text={copied ? t('copied') : t('copy')}
                      className="ml-2 px-4 py-2 whitespace-nowrap"
                    />
                  </div>

                  <div className="mt-4 flex justify-center">
                    <button
                      type="button"
                      onClick={() => {
                        setShareUrl('');
                        setCopied(false);
                      }}
                      className="text-sm text-primary-studio underline"
                    >
                      {t('create_new_link')}
                    </button>
                  </div>
                </div>
              )}

              <div className="flex justify-center mt-6">
                <ButtonWithLoading
                  type="button"
                  onClick={onClose}
                  text={tg('close')}
                  outline
                  disabled={isCreating}
                  className="px-4 py-2"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}));

export default ShareCartModal;